import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, ShieldCheck, Globe2, Award, ChevronLeft, ChevronRight, CheckCircle2, Sparkles, Clock, Check } from "lucide-react";
import { FadeIn, Stagger, StaggerItem, Counter } from "../motion";
import { METRICS, SERVICES, PROCESS, WHY_US, CLIENTS, IMAGES, ABOUT_PARAS } from "../../data/content";

const MARQUEE_CLIENTS = [0, 1].flatMap((loop) =>
  CLIENTS.map((name) => ({ id: `${loop}-${name}`, name }))
);

export const HomeHero = () => (
  <section className="relative bg-[#4A1136] overflow-hidden" data-testid="hero-section">
    <img src="/assets/bg-home.jpg" alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-r from-[#3A0D2A]/[0.98] via-[#4A1136]/[0.94] to-[#4A1136]/80" />
    <div className="absolute inset-0 hero-pattern opacity-40 pointer-events-none" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-20 lg:pt-40 lg:pb-28 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-14 items-center relative z-10">
      <div>
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="inline-flex items-center gap-2.5 border border-[#D4AF37]/50 bg-[#3A0D2A]/60 backdrop-blur-md text-[#D4AF37] px-4 py-1.5 rounded-full font-mono text-[11px] sm:text-xs tracking-[0.2em] uppercase mb-7 shadow-sm"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#D4AF37] opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#D4AF37]"></span>
          </span>
          <ShieldCheck size={14} className="text-[#D4AF37]" />
          <span>ISO Certified · MEA Registered · Est. 2000</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-heading font-extrabold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
        >
          Trusted partner<br />in <span className="text-[#D4AF37] drop-shadow-sm">your success.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.22 }}
          className="mt-6 text-white/80 text-base md:text-lg leading-relaxed max-w-xl font-normal"
        >
          A leading global recruitment and staffing solutions provider with over two decades of expertise — mobilizing 40,000+ candidates across the Middle East, Eastern Europe, South Asia, and the Caribbean.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.34 }}
          className="mt-9 flex flex-wrap gap-4 items-center"
        >
          <Link
            to="/contact"
            className="btn-shine inline-flex items-center gap-2 bg-[#D4AF37] text-[#4A1136] px-8 py-4 font-bold tracking-wide transition-all duration-300 hover:bg-white hover:text-[#811F5D] hover:gap-3 active:scale-[0.97] shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-[#811F5D] focus:ring-offset-2 rounded-xl text-sm sm:text-base"
            data-testid="hero-cta-hire"
          >
            Hire Workforce <ArrowUpRight size={18} />
          </Link>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 border border-white/35 bg-white/5 backdrop-blur-sm text-white px-7 py-4 font-semibold tracking-wide transition-all duration-300 hover:border-[#D4AF37] hover:bg-[#D4AF37]/15 hover:text-[#D4AF37] active:scale-[0.97] focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:ring-offset-2 rounded-xl text-sm sm:text-base"
            data-testid="hero-cta-services"
          >
            Explore Services <ArrowRight size={18} />
          </Link>
        </motion.div>

        {/* Micro-trust proof badges */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.45 }}
          className="mt-9 pt-7 border-t border-white/10 flex flex-wrap gap-x-6 gap-y-2 text-xs font-medium text-white/75"
        >
          <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#D4AF37]" /> 40,000+ Mobilized</span>
          <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#D4AF37]" /> 150+ Enterprise Clients</span>
          <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#D4AF37]" /> Timed 24-Day Workflow</span>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.25 }}
        className="relative hidden md:block"
      >
        <div className="border-2 border-[#D4AF37]/50 rounded-2xl absolute -top-4 -left-4 right-8 bottom-8 pointer-events-none" />
        <div className="img-zoom relative rounded-2xl overflow-hidden shadow-2xl border border-white/10">
          <img
            src={IMAGES.hero}
            alt="Vardhamaan recruitment team"
            className="w-full h-[420px] object-cover"
            data-testid="hero-image"
          />
        </div>
        <motion.div
          animate={{ y: [0, -8, 0] }}
          transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
          className="absolute -bottom-6 -left-8 bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl px-6 py-4 flex items-center gap-4 border border-[#811F5D]/15"
          data-testid="hero-floating-badge"
        >
          <span className="font-heading font-extrabold text-3xl text-[#811F5D]">
            <Counter value={40000} />
          </span>
          <span className="text-xs font-bold text-[#2A0E20] leading-tight uppercase tracking-wider">
            Candidates<br /><span className="text-[#811F5D]">Mobilized</span>
          </span>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

export const MetricsSection = () => (
  <section className="bg-[#2A0E20] border-y border-[#D4AF37]/25 relative z-10" data-testid="metrics-section">
    <Stagger className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid grid-cols-2 lg:grid-cols-4 gap-8">
      {METRICS.map((metric, idx) => (
        <StaggerItem
          key={metric.label}
          className={`text-center lg:text-left ${idx !== METRICS.length - 1 ? "lg:border-r lg:border-white/10 lg:pr-6" : ""}`}
          data-testid={`metric-${metric.label.toLowerCase().replaceAll(" ", "-")}`}
        >
          <p className="font-heading font-extrabold text-4xl lg:text-5xl text-[#D4AF37] tracking-tight">
            <Counter value={metric.value} suffix={metric.suffix} />
          </p>
          <p className="mt-2 text-white/70 text-sm font-medium tracking-wide">{metric.label}</p>
        </StaggerItem>
      ))}
    </Stagger>
  </section>
);

export const ClientsMarquee = () => (
  <section className="bg-[#FAF7F2] py-8 overflow-hidden border-b border-[#811F5D]/10 relative" data-testid="clients-marquee">
    {/* Gradient edge masks for smooth fade UX */}
    <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#FAF7F2] to-transparent z-10" />
    <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#FAF7F2] to-transparent z-10" />
    <div className="marquee flex gap-14 whitespace-nowrap">
      {MARQUEE_CLIENTS.map((client) => (
        <span key={client.id} className="font-heading font-bold text-[#811F5D]/55 hover:text-[#811F5D] transition-colors text-lg tracking-wide uppercase flex items-center gap-14 cursor-default select-none">
          {client.name} <span className="text-[#D4AF37]">◆</span>
        </span>
      ))}
    </div>
  </section>
);

export const HomePartners = () => (
  <section className="bg-gradient-to-b from-[#FAF7F2] to-white py-20 border-b border-[#811F5D]/10" data-testid="home-partners-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <span className="font-mono text-xs text-[#811F5D] tracking-[0.2em] uppercase font-bold inline-flex items-center gap-1.5 mb-3">
          <ShieldCheck size={14} className="text-[#D4AF37]" /> Trusted Sourcing Network
        </span>
        <h3 className="font-heading font-extrabold text-2xl sm:text-3xl text-[#2A0E20] tracking-tight">
          Approved Manpower Partner for Global Enterprises
        </h3>
      </div>
      
      <div className="relative group max-w-4xl mx-auto">
        <div className="absolute -inset-1.5 bg-gradient-to-r from-[#811F5D]/20 via-[#D4AF37]/30 to-[#811F5D]/20 rounded-3xl blur opacity-30 group-hover:opacity-60 transition duration-1000 group-hover:duration-200" />
        
        <div className="relative overflow-hidden rounded-2xl border border-[#811F5D]/10 bg-white p-4 sm:p-6 shadow-xl hover:shadow-2xl transition-all duration-300 transform group-hover:-translate-y-1">
          <div className="overflow-hidden rounded-xl border border-white/50 bg-[#FAF7F2]">
            <img
              src="/assets/clients-grid.png"
              alt="Vardhamaan partners and client logos including United Precast Concrete, Leminar, Global, Rayneau, Leminar Global, Carisun, Technical Experts, TTE, etc."
              className="w-full h-auto object-cover transition-transform duration-700 group-hover:scale-[1.01]"
              data-testid="home-partners-logo-grid"
            />
          </div>
          
          <div className="mt-5 flex items-center justify-center gap-2 text-xs font-semibold text-[#2A0E20]/70">
            <ShieldCheck size={16} className="text-[#D4AF37]" />
            <span>Registered, Audited & Compliant Workforce Mobilization</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const AboutTeaser = () => (
  <section className="bg-[#FAF7F2]" data-testid="about-teaser-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-14 items-center">
      <FadeIn className="relative order-2 lg:order-1">
        <div className="img-zoom rounded-2xl overflow-hidden shadow-xl border border-[#811F5D]/10">
          <img src={IMAGES.about} alt="Vardhamaan office collaboration" className="w-full h-[400px] object-cover" data-testid="about-teaser-image" />
        </div>
        <div className="absolute -bottom-5 -right-5 bg-[#811F5D] text-white px-6 py-4 rounded-xl flex items-center gap-3 shadow-xl border border-[#D4AF37]/30">
          <Award size={26} className="text-[#D4AF37]" />
          <span className="text-sm font-semibold leading-tight">ISO-Certified<br />International Organization</span>
        </div>
      </FadeIn>
      <FadeIn delay={0.1} className="order-1 lg:order-2">
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#811F5D] font-bold mb-4">01 / Who We Are</p>
        <h2 className="font-heading font-extrabold text-[#2A0E20] text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-tight">
          From a manpower startup to a <span className="text-[#811F5D]">global HR powerhouse.</span>
        </h2>
        <p className="mt-6 text-[#4B3A45] leading-relaxed text-base">{ABOUT_PARAS[0]}</p>
        <div className="mt-6 flex flex-wrap gap-4 text-xs font-semibold text-[#811F5D]">
          <span className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-[#811F5D]/15 shadow-sm"><Check size={14} className="text-[#D4AF37]" /> MEA Certified Agency</span>
          <span className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-[#811F5D]/15 shadow-sm"><Check size={14} className="text-[#D4AF37]" /> 4 Physical Offices</span>
          <span className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-[#811F5D]/15 shadow-sm"><Check size={14} className="text-[#D4AF37]" /> Global Sourcing Hub</span>
        </div>
        <Link
          to="/about"
          className="mt-8 inline-flex items-center gap-2 font-bold text-[#811F5D] border-b-2 border-[#D4AF37] pb-1 transition-all duration-300 hover:gap-4 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-[#811F5D] focus:ring-offset-2"
          data-testid="about-teaser-link"
        >
          Read our full story <ArrowRight size={17} />
        </Link>
      </FadeIn>
    </div>
  </section>
);

export const ServicesOverview = () => (
  <section className="bg-white" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
      <FadeIn className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <div>
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#811F5D] font-bold mb-4">02 / What We Do</p>
          <h2 className="font-heading font-extrabold text-[#2A0E20] text-2xl sm:text-3xl lg:text-4xl tracking-tight">
            End-to-end workforce solutions<br />— all under one roof.
          </h2>
        </div>
        <Link
          to="/services"
          className="inline-flex items-center gap-2 font-bold text-[#811F5D] border-b-2 border-[#D4AF37] pb-1 transition-all duration-300 hover:gap-4 active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-[#811F5D] focus:ring-offset-2"
          data-testid="services-view-all-link"
        >
          View all services <ArrowRight size={17} />
        </Link>
      </FadeIn>
      <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {SERVICES.map((service) => (
          <StaggerItem key={service.slug}>
            <Link
              to="/services"
              className="group block h-full rounded-2xl border border-[#811F5D]/15 p-8 transition-all duration-300 hover:border-[#811F5D] hover:shadow-xl hover:-translate-y-1.5 active:scale-[0.99] bg-white relative overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#811F5D] focus:ring-offset-2"
              data-testid={`service-card-${service.slug}`}
            >
              <div className="h-1 w-0 bg-gradient-to-r from-[#811F5D] to-[#D4AF37] absolute top-0 left-0 transition-all duration-300 group-hover:w-full" />
              <div className="flex items-start justify-between mb-6">
                <span className="font-mono text-xs px-2.5 py-1 rounded-md bg-[#FAF7F2] text-[#811F5D] font-bold border border-[#811F5D]/10">
                  {service.num}
                </span>
                <div className="w-8 h-8 rounded-full bg-[#FAF7F2] flex items-center justify-center transition-all duration-300 group-hover:bg-[#811F5D] group-hover:text-white">
                  <ArrowUpRight size={16} className="text-[#811F5D] transition-transform duration-300 group-hover:text-white group-hover:rotate-45" />
                </div>
              </div>
              <h3 className="font-heading font-bold text-[#2A0E20] text-lg leading-snug group-hover:text-[#811F5D] transition-colors">
                {service.title}
              </h3>
              <p className="mt-3 text-sm text-[#6B5A64] leading-relaxed">{service.short}</p>
            </Link>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

export const ProcessSection = () => (
  <section className="bg-[#4A1136] relative overflow-hidden" data-testid="process-section">
    <div className="absolute inset-0 hero-pattern opacity-30 pointer-events-none" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28 relative z-10">
      <FadeIn className="mb-14">
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#D4AF37] font-bold mb-4">03 / Our Recruitment Process</p>
        <h2 className="font-heading font-extrabold text-white text-2xl sm:text-3xl lg:text-4xl tracking-tight">
          Deployment-ready in a<br /><span className="text-[#D4AF37]">timed 24-day workflow.</span>
        </h2>
      </FadeIn>
      <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {PROCESS.map((step, idx) => (
          <StaggerItem
            key={step.title}
            className="bg-[#3A0D2A]/85 backdrop-blur-md rounded-2xl border border-[#D4AF37]/20 p-8 group transition-all duration-300 hover:bg-[#5A1743] hover:-translate-y-1 shadow-md hover:shadow-xl"
            data-testid={`process-step-${step.days}-${step.title.toLowerCase().replaceAll(" ", "-")}`}
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-baseline gap-2">
                <span className="font-heading font-extrabold text-4xl lg:text-5xl text-[#D4AF37] group-hover:scale-105 inline-block transition-transform duration-300 origin-left">
                  {step.days}
                </span>
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/60">{step.unit}</span>
              </div>
              <span className="font-mono text-xs text-white/40">Phase 0{idx + 1}</span>
            </div>
            <h3 className="font-heading font-bold text-white text-lg">{step.title}</h3>
            <p className="mt-2 text-sm text-white/70 leading-relaxed">{step.text}</p>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

export const WhyUsSection = () => (
  <section className="bg-[#FAF7F2]" data-testid="why-us-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28">
      <FadeIn className="mb-14 max-w-2xl">
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#811F5D] font-bold mb-4">04 / Why Vardhamaan</p>
        <h2 className="font-heading font-extrabold text-[#2A0E20] text-2xl sm:text-3xl lg:text-4xl tracking-tight">
          Four reasons global employers keep coming back.
        </h2>
      </FadeIn>
      <Stagger className="grid md:grid-cols-2 gap-8">
        {WHY_US.map((reason, position) => (
          <StaggerItem
            key={reason.title}
            className="bg-white rounded-2xl border-l-4 border-[#D4AF37] border border-[#811F5D]/10 p-8 shadow-sm transition-all duration-300 hover:shadow-xl hover:border-l-[#811F5D] hover:-translate-y-1"
            data-testid={`why-us-card-${reason.title.toLowerCase().replaceAll(" ", "-")}`}
          >
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono text-xs text-[#D4AF37] font-bold bg-[#FAF7F2] px-2.5 py-1 rounded border border-[#D4AF37]/20">0{position + 1}</span>
              <ShieldCheck size={18} className="text-[#811F5D]/30" />
            </div>
            <h3 className="font-heading font-bold text-[#2A0E20] text-lg mt-2 uppercase tracking-wide">{reason.title}</h3>
            <p className="mt-3 text-sm text-[#6B5A64] leading-relaxed">{reason.text}</p>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  </section>
);

export const HomeCta = () => (
  <section className="bg-gradient-to-r from-[#811F5D] to-[#4A1136] relative overflow-hidden" data-testid="cta-band">
    <div className="absolute inset-0 hero-pattern opacity-25 pointer-events-none" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 flex flex-wrap items-center justify-between gap-8 relative z-10">
      <FadeIn>
        <div className="flex items-center gap-3 text-[#D4AF37] mb-3">
          <Globe2 size={20} />
          <span className="font-mono text-xs tracking-[0.3em] uppercase font-bold">Global Reach · Local Expertise</span>
        </div>
        <h2 className="font-heading font-extrabold text-white text-2xl sm:text-3xl lg:text-4xl tracking-tight max-w-xl">
          Need skilled manpower for your next project?
        </h2>
      </FadeIn>
      <FadeIn delay={0.15}>
        <Link
          to="/contact"
          className="btn-shine inline-flex items-center gap-2 bg-[#D4AF37] text-[#4A1136] px-8 py-4 font-bold tracking-wide transition-all duration-300 hover:bg-white hover:text-[#811F5D] hover:gap-3 active:scale-[0.97] shadow-lg hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-[#811F5D] focus:ring-offset-2 rounded-xl text-sm sm:text-base"
          data-testid="cta-band-contact-link"
        >
          Get in touch <ArrowUpRight size={18} />
        </Link>
      </FadeIn>
    </div>
  </section>
);


export const HomeDeployments = () => {
  const total = 18;
  const [activeIndex, setActiveIndex] = useState(4); // Start with deployment (5)

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % total);
    }, 3000);
    return () => clearInterval(timer);
  }, []);

  const handlePrev = () => {
    setActiveIndex((prev) => (prev - 1 + total) % total);
  };

  const handleNext = () => {
    setActiveIndex((prev) => (prev + 1) % total);
  };

  return (
    <section className="bg-white py-20 border-t border-[#811F5D]/10" data-testid="home-deployments-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <span className="font-mono text-xs text-[#811F5D] tracking-[0.2em] uppercase font-bold inline-flex items-center gap-1.5 mb-3">
            <ShieldCheck size={14} className="text-[#D4AF37]" /> Sourcing in Action
          </span>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl text-[#2A0E20] tracking-tight">
            Successful Deployments
          </h2>
          <p className="mt-4 text-[#2A0E20]/75 leading-relaxed text-base">
            Glimpses of our mobilized workforce and successful deployments dispatched globally to power client operations.
          </p>
        </div>

        <div className="relative group max-w-4xl mx-auto px-10">
          {/* Floating Left Button */}
          <button
            onClick={handlePrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-10 h-10 rounded-full bg-white/90 hover:bg-white text-[#811F5D] border border-[#811F5D]/10 hover:text-[#D4AF37] shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#811F5D]"
            aria-label="Previous Slide"
          >
            <ChevronLeft size={20} />
          </button>

          {/* Floating Right Button */}
          <button
            onClick={handleNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 flex items-center justify-center w-10 h-10 rounded-full bg-white/90 hover:bg-white text-[#811F5D] border border-[#811F5D]/10 hover:text-[#D4AF37] shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#811F5D]"
            aria-label="Next Slide"
          >
            <ChevronRight size={20} />
          </button>

          {/* Outer glowing border */}
          <div className="absolute -inset-1.5 bg-gradient-to-r from-[#811F5D]/20 via-[#D4AF37]/30 to-[#811F5D]/20 rounded-3xl blur opacity-30 group-hover:opacity-60 transition duration-1000 group-hover:duration-200" />
          
          {/* Main Card */}
          <div className="relative overflow-hidden rounded-2xl border border-[#811F5D]/10 bg-white p-4 sm:p-6 shadow-xl hover:shadow-2xl transition-all duration-300 transform group-hover:-translate-y-1">
            <div className="overflow-hidden rounded-xl border border-white/50 bg-[#FAF7F2] relative aspect-[16/10] sm:h-[450px]">
              <img
                src={`/assets/deployment (${activeIndex + 1}).jpeg`}
                alt={`Vardhamaan successful candidate deployment ${activeIndex + 1}`}
                className="w-full h-full object-cover transition-all duration-500"
                data-testid="home-deployment-image"
              />
            </div>
            
            <div className="mt-5 flex items-center justify-center gap-2 text-xs font-semibold text-[#2A0E20]/60">
              <ShieldCheck size={16} className="text-[#D4AF37]" />
              <span>On-site Handover & Candidate Verification Mobilization (Image {activeIndex + 1} of 18)</span>
            </div>

            {/* Indicator dots inside the card for cleaner layout */}
            <div className="mt-5 flex justify-center gap-1.5">
              {Array.from({ length: total }).map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveIndex(idx)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${activeIndex === idx ? "w-5 bg-[#811F5D]" : "w-1.5 bg-[#811F5D]/20 hover:bg-[#811F5D]/40"}`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};